import React from 'react';
import { Spinner } from '../../components/ui';
import { fmtDate, fmtTime, isPastFunction } from '../../utils/helpers';
import FunctionForm from './FunctionForm';

const weekday = (iso) => {
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { weekday: 'short' });
};
const mapUrl = (f) => {
  const q = f.GpsLocation || [f.Venue, f.VenueAddress, f.City].filter(Boolean).join(', ');
  return q ? `https://maps.google.com/?q=${encodeURIComponent(q)}` : '';
};
const statusClass = (s) => (s === 'Confirmed' ? 'chip ok' : s === 'Cancelled' ? 'chip danger' : s === 'Done' ? 'chip' : 'chip pend');

// Desktop Functions — ONE compact table, one line per function: date + weekday
// (shown once for a day with several functions), function + type, time, venue
// + address (map link), guests invited, status. Click a row → its guests.
// Add/Edit/Del are Owner / Event-Admin only. Functions that are over are dimmed;
// two that overlap on one day are tinted.
export default function FunctionsDesktop({ vm }) {
  const { filtered, days, loading, can, q, setQ, startAdd, startEdit, del, countsFor, isTwoSided, hidePast, setHidePast, pastCount, shownCount,
    setStatus, savingStatus, FUNCTION_STATUSES } = vm;
  const showActions = can.edit || can.delete;

  return (
    <div className="functions-page">
      <div className="page-head">
        <h1>Functions</h1>
        <span className="count-pill">{shownCount}</span>
        {pastCount > 0 && (
          <label className="fn-hide-past" title="Functions whose date has passed">
            <input type="checkbox" checked={hidePast} onChange={(e) => setHidePast(e.target.checked)} />
            Hide functions that are over{hidePast ? ` (${pastCount} hidden)` : ''}
          </label>
        )}
        <div className="spacer" />
        <input className="fn-search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
        {can.add && <button className="btn primary" onClick={startAdd}>+ Add Function</button>}
      </div>

      {loading ? <div className="card card-pad"><Spinner /></div> : filtered.length === 0 ? (
        <div className="card card-pad empty">No functions yet.{!can.add && ' Ask the Event Owner to add functions.'}</div>
      ) : (
        <div className="card table-wrap">
          <table className="tbl fn-table">
            <colgroup>
              <col style={{ width: 128 }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: 160 }} />
              <col />
              <col style={{ width: 250 }} />
              <col style={{ width: 96 }} />
              {showActions && <col style={{ width: 118 }} />}
            </colgroup>
            <thead>
              <tr>
                <th>Date</th>
                <th>Function</th>
                <th>Time</th>
                <th>Venue</th>
                <th title="Guests invited: persons · parties (cards / households) · extra-card guests · Groom / Bride">Guests invited</th>
                <th>Status</th>
                {showActions && <th />}
              </tr>
            </thead>
            <tbody>
              {days.flatMap(day => day.items.map((row, i) => {
                const c = countsFor(row);
                const past = isPastFunction(row);
                const clash = day.clashes.has(row.FunctionId);
                const type = row.FunctionTypeName || row.FunctionType || '';
                const time = [row.StartTime || row.FunctionTime, row.EndTime].filter(Boolean).map(fmtTime).join(' – ');
                const address = [row.VenueAddress, row.City].filter(Boolean).join(', ').replace(/\s*\n\s*/g, ' ');
                return (
                  <tr key={row.FunctionId} className={'row-link' + (past ? ' fn-past' : '') + (clash ? ' fn-clash' : '') + (i === 0 ? ' fn-day-first' : '')}
                    title={clash ? 'This time overlaps another function on the same day' : 'Open the guests of this function'}
                    onClick={() => vm.openRow(row)}>
                    {i === 0 && (
                      <td className="fn-date" rowSpan={day.items.length}>
                        <strong>{day.date ? fmtDate(day.date) : 'No date'}</strong>
                        {day.date && <span className="muted"> {weekday(day.date)}</span>}
                        {day.clashes.size > 0 && <span className="fn-warn" title="Two functions on this day overlap in time"> ⚠</span>}
                      </td>
                    )}
                    <td className="fn-one">
                      <strong>{row.FunctionName}</strong>
                      {type && type !== row.FunctionName && <span className="muted"> · {type}</span>}
                      {past && <span className="chip" style={{ marginLeft: 6 }}>over</span>}
                    </td>
                    <td className="fn-time">{time || '—'}</td>
                    <td className="fn-one" title={[row.Venue, address].filter(Boolean).join(' — ')}>
                      <strong>{row.Venue || '—'}</strong>
                      {mapUrl(row) && <a href={mapUrl(row)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} title="Open in Google Maps"> 📍</a>}
                      {address && <span className="muted"> {address}</span>}
                    </td>
                    <td className="fn-one fn-guests">
                      <strong>{c.persons}</strong>
                      <span className="muted"> persons · {c.parties} {c.parties === 1 ? 'party' : 'parties'}</span>
                      {c.extra > 0 && <span className="muted"> · +{c.extra} extra</span>}
                      {isTwoSided && (c.bySide.Groom || c.bySide.Bride) ? <span className="muted"> · G {c.bySide.Groom || 0} / B {c.bySide.Bride || 0}</span> : null}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {can.edit ? (
                        <select className={'fn-status ' + statusClass(row.Status)} value={row.Status || 'Planned'}
                          disabled={savingStatus === row.FunctionId} title="Change the status"
                          onChange={(e) => setStatus(row, e.target.value)}>
                          {FUNCTION_STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
                        </select>
                      ) : <span className={statusClass(row.Status)}>{row.Status || 'Planned'}</span>}
                    </td>
                    {showActions && (
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {can.edit && <button className="btn light sm" onClick={() => startEdit(row)}>Edit</button>}
                        {can.delete && <button className="btn danger sm" style={{ marginLeft: 6 }} onClick={() => del(row)}>Del</button>}
                      </td>
                    )}
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
      )}

      <FunctionForm vm={vm} event={vm.event} />
    </div>
  );
}
